import { useQuery } from '@tanstack/react-query';
import { buildSeoReport } from '@/components/seo/buildReport';
import { isSeoSeries, type SeoCatalog, type SeoReport, type SeoSeries } from '@/components/seo/types';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export function useSeoAudit(weeks: number, days = 0) {
    const grain = days > 0 ? 'day' : 'week';
    return useQuery<SeoReport>({
        queryKey: ['admin', 'seo-gsc', weeks, days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<SeoSeries>('get_seo_gsc_audit', {
                p_weeks: weeks,
                p_days: days,
            });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const first = rows[0];
            const payload = first && typeof first === 'object' && 'weeks' in first
                ? first
                : first && typeof first === 'object' && 'get_seo_gsc_audit' in first
                    ? (first as { get_seo_gsc_audit: SeoSeries }).get_seo_gsc_audit
                    : first;
            if (!isSeoSeries(payload)) throw new Error('La serie SEO no tiene el formato esperado');
            return buildSeoReport(payload, grain, await loadCatalog());
        },
        staleTime: 60_000,
    });
}

async function loadCatalog(): Promise<SeoCatalog | undefined> {
    const { data, error } = await dbClient.rpc<SeoCatalog | { admin_seo_catalog_groups: SeoCatalog }>('admin_seo_catalog_groups');
    if (error) return undefined;
    const rows = normalizeRpcResponse(data);
    const first = rows[0];
    const payload = first && typeof first === 'object' && 'blogTypes' in first
        ? first
        : first && typeof first === 'object' && 'admin_seo_catalog_groups' in first
            ? first.admin_seo_catalog_groups
            : undefined;
    if (!payload || typeof payload !== 'object') return undefined;
    return {
        blogTypes: stringMap(payload.blogTypes),
        storeBrands: stringMap(payload.storeBrands),
    };
}

function stringMap(value: unknown): Record<string, string> {
    if (!value || typeof value !== 'object') return {};
    const out: Record<string, string> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
        if (typeof item === 'string' && item.trim()) out[key] = item.trim();
    }
    return out;
}
